import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Typography,
	FormControl,
	FormLabel,
	RadioGroup,
	FormControlLabel,
	Radio,
	Divider,
	Avatar,
	Collapse,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import { uniq, cloneDeep } from "lodash";
import { ReactSortable, SortableEvent } from "react-sortablejs";
import { v4 } from "uuid";

import "./ProcurementMethodSelection.css";
import { RootState } from "redux/store";
import { ItemSourcingType } from "utility/enums/requisition-entry";
import { radioDefaultIcon, radioCheckedIcon } from "utility/icons";
import {
	collapseUpDefaultIcon,
	collapseDownDefaultIcon,
	editUnderlinePrimaryIcon,
	dragIcon,
} from "assets/images";
import { Props } from "./ProcurementMethodSelection.Props";
import {
	ItemDetailGroupByAvailability,
	ProcurementMethodSelectionItem,
} from "models/Requisition/RequisitionProcurementMethodSelection";
import { RequisitionService } from "services/requisition.service";
import { Shared } from "utility/shared";
import ProcurementMethodSelectionDialog from "./ProcurementMethodSelectionDialog";
import { IProcurementMethod } from "models/ProcurementMethod/procurementMethodModel";
import { fetchProcurementMethodList } from "redux/masterPageStateSlice";
import Emitter from "utility/emitter";
import {
	closedTenderProcMethod,
	openTenderProcMethod,
	ProcurementMethodUtil,
	singleSourcingTenderProcMethod,
	singleSourcingRfqProcMethod,
	widelyAvailability,
	widelyAvailabilityId,
} from "utility/procurementMethodSelectionUtil";
import ConfirmationDialog from "components/ConfirmationDialog";
import { UPDATE_REQUISITION_LINE_PROC_METHOD } from "utility/emitterEvents";

const ProcurementMethodSelection: React.FC<Props> = ({
	requisitionId,
	requisitionStepHistoryId,
	isEditMode,
	isViewMode,
}) => {
	const dispatch = useDispatch();
	const organizationDetails = useSelector(
		(state: RootState) => state.authState.organizationDetails
	);
	const procurementMethodList: IProcurementMethod[] = useSelector(
		(state: RootState) => state.masterPageState.procurementMethodList
	);
	const [itemSourcingTypeId, setItemSourcingTypeId] = useState<number | null>(
		null
	);
	const [panelList, setPanelList] = useState<string[]>([]);
	const [
		procurementMethodSelectionItemList,
		setProcurementMethodSelectionItemList,
	] = useState<ProcurementMethodSelectionItem[]>([]);
	const [itemListGroupBySplit, setItemListGroupBySplit] = useState<
		ItemDetailGroupByAvailability[]
	>([]); // set item list group by Split type
	const [itemListGroupByCombined, setItemListGroupByCombined] = useState<
		ItemDetailGroupByAvailability[]
	>([]); // set item list group by Combined type
	const [
		openProcurementMethodSelectionDialog,
		setOpenProcurementMethodSelectionDialog,
	] = useState<boolean>(false); // required for ProcurementMethodSelectionDialog
	const [selectedIndex, setSelectedIndex] = useState<number>(-1);
	const [
		openTransversalProcConfirmationDialog,
		setOpenTransversalProcConfirmationDialog,
	] = useState<boolean>(false); // required for Transversal Procurement Confirmation

	useEffect(() => {
		dispatch(fetchProcurementMethodList(false));
	}, [dispatch]);

	const isItemSourcingTypeCombined = useMemo(
		() =>
			itemSourcingTypeId
				? itemSourcingTypeId === ItemSourcingType.Combined
				: false,
		[itemSourcingTypeId]
	);

	const itemWithTransversalContract = useMemo(() => {
		return procurementMethodList.filter((p) => p.isTransversalContract).length >
			0
			? procurementMethodSelectionItemList.find((p) => p.isTransversalContract)
					?.item || ""
			: "";
	}, [procurementMethodList, procurementMethodSelectionItemList]);

	const setUpdateProcurementMethodList = (
		itemList: ItemDetailGroupByAvailability[],
		isSourcingTypeCombined = false
	) => {
		if (isEditMode && requisitionId > -1) {
			const tempList = ProcurementMethodUtil.getUpdateProcurementMethodList(
				itemList,
				isSourcingTypeCombined
					? ItemSourcingType.Combined
					: ItemSourcingType.Split
			);
			Emitter.off(UPDATE_REQUISITION_LINE_PROC_METHOD, {} as any);
			Emitter.emit(UPDATE_REQUISITION_LINE_PROC_METHOD, {
				requisitionId: requisitionId,
				procurementMethodList: tempList,
			});
		}
	};

	// fetch RequisitionProcurementMethodSelectionList on page load using requisitionId & requisitionStepHistoryId
	useEffect(() => {
		const getRequisitionProcurementMethodSelectionList = () => {
			RequisitionService.getRequisitionProcurementMethodSelectionList(
				requisitionId,
				requisitionStepHistoryId
			)
				.then((result) => {
					if (result) {
						setProcurementMethodSelectionItemList(result);
					}
				})
				.catch((err) => {
					Shared.showSnackbarMessage(
						"Error while loading data for procurement method selection"
					);
					setProcurementMethodSelectionItemList([]);
				});
		};
		if (requisitionId > 0 && requisitionStepHistoryId > 0)
			getRequisitionProcurementMethodSelectionList();
		return () => {
			setProcurementMethodSelectionItemList([]);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [requisitionId, requisitionStepHistoryId]);

	// set item list by sourcing type on page load
	useEffect(() => {
		if (
			procurementMethodList.length &&
			procurementMethodSelectionItemList.length
		) {
			const tempTypeId = ProcurementMethodUtil.getItemSourcingTypeId(
				procurementMethodSelectionItemList
			);
			setItemSourcingTypeId(tempTypeId);
			const itemList = ProcurementMethodUtil.getItemListBySourcingType(
				procurementMethodSelectionItemList,
				procurementMethodList,
				isEditMode
			);
			setItemListGroupBySplit(itemList[0]);
			setItemListGroupByCombined(itemList[1]);
			if (!isEditMode) return;
			setTimeout(() => {
				if (tempTypeId === ItemSourcingType.Combined) {
					if (itemWithTransversalContract) {
						setOpenTransversalProcConfirmationDialog(true);
					} else {
						setUpdateProcurementMethodList(itemList[1]);
					}
				} else {
					setUpdateProcurementMethodList(itemList[0], false);
				}
			}, 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [procurementMethodList, procurementMethodSelectionItemList]);

	const disableItemSourcingType = useMemo(() => {
		return !!(
			!isEditMode ||
			uniq(procurementMethodSelectionItemList.map((p) => p.itemAvailability))
				.length === 1
		);
	}, [procurementMethodSelectionItemList, isEditMode]);

	const handlePanelOpenClose = (id: string) => {
		if (panelList.includes(id)) {
			setPanelList(panelList.filter((p) => p !== id));
		} else {
			setPanelList(panelList.concat(id));
		}
	};

	// get requisition total amount
	const getRequisitionTotal = () => {
		const subItemList = (
			isItemSourcingTypeCombined
				? itemListGroupByCombined
				: itemListGroupBySplit
		).flatMap((i) => i.items);
		return Shared.getFormattedCurrency(
			subItemList.reduce((prevValue, currentValue) => {
				return prevValue + currentValue.totalEstCost;
			}, 0)
		);
	};

	// handle MethodSelection checkbox change
	const handleMethodSelectionChange = (index: number) => {
		// open procurement method selection dialog
		setSelectedIndex(index);
		setOpenProcurementMethodSelectionDialog(true);
	};

	// handle ProcurementMethodSelectionDialog close event
	const handleMethodSelectionDialogClose = (model?: {
		procurementMethodId: number | null;
		comment: string;
	}) => {
		const tempItemList = isItemSourcingTypeCombined
			? [...itemListGroupByCombined]
			: [...itemListGroupBySplit];
		if (model && model.procurementMethodId && model.comment) {
			tempItemList[selectedIndex].procurementMethod =
				procurementMethodList.find(
					(p) => p.procurementMethodId === model.procurementMethodId
				)?.procurementMethodCategory || "";
			tempItemList[selectedIndex].procurementMethodId =
				model.procurementMethodId;
			tempItemList[selectedIndex].comment = model.comment;
		}
		if (isItemSourcingTypeCombined) setItemListGroupByCombined(tempItemList);
		else setItemListGroupBySplit(tempItemList);
		setOpenProcurementMethodSelectionDialog(false);
		setSelectedIndex(-1);
		setUpdateProcurementMethodList(tempItemList, isItemSourcingTypeCombined);
	};

	const disableEditPRMethodIcon = (procMethodId: number | null) => {
		if (!isItemSourcingTypeCombined) return false;
		const procMethod = procurementMethodList.find(
			(p) => p.procurementMethodId === procMethodId
		);
		return procMethod && procMethod.procurementMethodAbbreviation
			? procMethod.procurementMethodAbbreviation !== openTenderProcMethod &&
					procMethod.procurementMethodAbbreviation !== closedTenderProcMethod &&
					procMethod.procurementMethodAbbreviation !==
						singleSourcingTenderProcMethod &&
					procMethod.procurementMethodAbbreviation !==
						singleSourcingRfqProcMethod
			: false;
	};

	const handleItemDragDrop = (event: SortableEvent) => {
		const updateItemList = (
			tempList: ItemDetailGroupByAvailability[],
			index: number
		) => {
			const ProcMethodObj = tempList[index].items.length
				? ProcurementMethodUtil.getProcurementMethodFromTotal(
						ProcurementMethodUtil.getTotalByItemList(tempList[index].items),
						tempList[index].items[0].itemAvailabilityId,
						procurementMethodList
				  )
				: undefined;
			tempList[index].procurementMethod = ProcMethodObj
				? ProcMethodObj.procurementMethodCategory
				: "";
			tempList[index].procurementMethodId = ProcMethodObj
				? ProcMethodObj.procurementMethodId
				: null;
			return tempList[index];
		};

		if (event.from.id !== event.to.id) {
			const removeFromListIndex = Number(event.from.id.split("-")[1]); // get list index from which item is removed
			const addToListIndex = Number(event.to.id.split("-")[1]); // get list index in which item is added
			const subItemIndex = Number(event.item.id.split("-")[1]); // get moved item index
			const tempList = [...itemListGroupBySplit];
			if (subItemIndex > -1 && addToListIndex > -1 && addToListIndex > -1) {
				const item = tempList[removeFromListIndex].items[subItemIndex]; // get item which is moved
				tempList[removeFromListIndex].items.splice(subItemIndex, 1); // remove moved item from list
				// add moved item into list
				tempList[addToListIndex].items.push({
					...item,
					itemAvailability: tempList[addToListIndex].itemAvailability,
					itemAvailabilityId: tempList[addToListIndex].itemAvailabilityId,
				});
				// update value of procurement method type, procurement method Id
				tempList[addToListIndex] = updateItemList(tempList, addToListIndex);
				tempList[removeFromListIndex] = updateItemList(
					tempList,
					removeFromListIndex
				);
				// after item is moved, it item length is 0, remove entire group from the list
				// if (!tempList[removeFromListIndex].items.length) {
				// 	tempList.splice(removeFromListIndex, 1);
				// }
				setItemListGroupBySplit(tempList);
				setUpdateProcurementMethodList(tempList, false);
			}
		}
	};

	const handleCombineTypeSourcingTypeSelection = () => {
		const tempItemList = cloneDeep([...itemListGroupByCombined]);
		const itemAvailabilityId =
			uniq(tempItemList.map((r) => r.itemAvailability)).length === 1
				? tempItemList[0].itemAvailabilityId
				: widelyAvailabilityId;
		const tempAvailability =
			itemAvailabilityId === widelyAvailabilityId
				? widelyAvailability
				: tempItemList[0].itemAvailability;
		const ProcMethodObj = tempItemList.length
			? ProcurementMethodUtil.getProcurementMethodFromTotal(
					ProcurementMethodUtil.getTotalByItemList(tempItemList[0].items),
					itemAvailabilityId,
					procurementMethodList
			  )
			: undefined;
		tempItemList[0].procurementMethod = ProcMethodObj
			? ProcMethodObj.procurementMethodCategory
			: "";
		tempItemList[0].procurementMethodId = ProcMethodObj
			? ProcMethodObj.procurementMethodId
			: null;
		tempItemList[0].itemAvailability = tempAvailability;
		tempItemList[0].itemAvailabilityId = itemAvailabilityId;
		tempItemList[0].items.forEach((item) => {
			item.itemAvailability = tempAvailability;
			item.itemAvailabilityId = itemAvailabilityId;
		});
		setItemListGroupByCombined(tempItemList);
		setUpdateProcurementMethodList(tempItemList);
	};

	const handleItemSourcingTypeChange = (typeId: number) => {
		if (typeId === ItemSourcingType.Combined) {
			// if any item exist with transversal contract flag, open TransversalProcConfirmationDialog
			if (itemWithTransversalContract) {
				setOpenTransversalProcConfirmationDialog(true);
			} else {
				handleCombineTypeSourcingTypeSelection();
			}
		} else {
			setUpdateProcurementMethodList(itemListGroupBySplit, false);
		}
		setItemSourcingTypeId(typeId);
	};

	const handleTransversalProcConfirmationDialogClose = (
		isConfirm: boolean | undefined
	) => {
		setOpenTransversalProcConfirmationDialog(false);
		if (isConfirm) {
			const tempItemList = [...itemListGroupByCombined];
			const procMethod = ProcurementMethodUtil.getTransversalProcMethod(
				procurementMethodList,
				ProcurementMethodUtil.getTotalByItemList(tempItemList[0].items)
			);
			if (procMethod) {
				tempItemList.forEach((item) => {
					item.procurementMethod = procMethod.procurementMethodCategory;
					item.procurementMethodId = procMethod.procurementMethodId;
				});
				setItemListGroupByCombined(tempItemList);
				setUpdateProcurementMethodList(tempItemList);
			}
		} else {
			handleCombineTypeSourcingTypeSelection();
		}
	};

	return (
		<div className="item-selection-panel">
			<div className="item-selection-header">
				<Typography variant="h4">Procurement Method Selection</Typography>
				{true ? (
					<div className="content">
						<FormControl className="radio-btn">
							<FormLabel id="demo-row-radio-buttons-group-label">
								Item Sourcing
							</FormLabel>
							<RadioGroup
								row
								value={itemSourcingTypeId}
								onChange={(event) =>
									handleItemSourcingTypeChange(Number(event.target.value))
								}
							>
								<FormControlLabel
									value={ItemSourcingType.Split}
									control={
										<Radio
											icon={radioDefaultIcon}
											checkedIcon={radioCheckedIcon}
											disabled={disableItemSourcingType || isViewMode}
										/>
									}
									label="Split"
								/>
								<FormControlLabel
									value={ItemSourcingType.Combined}
									control={
										<Radio
											icon={radioDefaultIcon}
											checkedIcon={radioCheckedIcon}
											disabled={disableItemSourcingType || isViewMode}
										/>
									}
									label={
										itemWithTransversalContract
											? "Combined/Transversal"
											: "Combined"
									}
								/>
							</RadioGroup>
						</FormControl>
						<div className="text-label-wrapper">
							<Typography variant="body1" className="light-grey-text">
								Requisition Total
							</Typography>
							<Typography variant="h4" className="default-text">
								{getRequisitionTotal()}
							</Typography>
						</div>
					</div>
				) : (
					<>
						<Divider />
						<Typography
							className="content"
							variant="body1"
							justifyContent="center"
							pt={2}
						>
							No Data Available
						</Typography>
					</>
				)}
			</div>
			{(isItemSourcingTypeCombined
				? itemListGroupByCombined
				: itemListGroupBySplit
			).map((item: ItemDetailGroupByAvailability, index: number) => (
				<TableContainer
					component={Paper}
					className="method-selection-table"
					key={v4()}
				>
					<div className="table-responsive">
						<Table className="collapsible-table procurement-selection-table">
							<TableHead
								style={{ visibility: index > 0 ? "collapse" : "visible" }}
							>
								<TableRow>
									<TableCell colSpan={2}></TableCell>
									<TableCell>No. of Items</TableCell>
									<TableCell>Total</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								<React.Fragment>
									<TableRow className="collapsible-row">
										<TableCell>
											<IconButton
												className="collapse-icon"
												size="small"
												onClick={() =>
													handlePanelOpenClose(item.itemAvailability)
												}
											>
												{panelList.includes(item.itemAvailability) ? (
													<Avatar src={collapseUpDefaultIcon} alt="Up Icon" />
												) : (
													<Avatar
														src={collapseDownDefaultIcon}
														alt="Down Icon"
													/>
												)}
											</IconButton>
										</TableCell>
										<TableCell className="req-id">
											{item.procurementMethod || "-"}
											{isEditMode &&
												item.procurementMethod &&
												organizationDetails.isAllowProcurementMethodChangeInRequisition && (
													<IconButton
														onClick={() => {
															handleMethodSelectionChange(index);
														}}
														disabled={
															disableEditPRMethodIcon(
																item.procurementMethodId
															) || isViewMode
														}
													>
														<Avatar
															variant="square"
															src={editUnderlinePrimaryIcon}
															alt="Change"
															sx={{ width: 15, height: 15 }}
															title="Change"
														/>
													</IconButton>
												)}
										</TableCell>
										<TableCell>{item.items.length}</TableCell>
										<TableCell>
											{Shared.getFormattedCurrency(
												ProcurementMethodUtil.getTotalByItemList(item.items)
											)}
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell style={{ padding: 0 }} colSpan={6}>
											<Collapse
												in={panelList.includes(item.itemAvailability)}
												timeout="auto"
												unmountOnExit
											>
												<Table className="fixed-table-layout">
													<TableHead>
														<TableRow>
															<TableCell></TableCell>
															<TableCell align="center">Item</TableCell>
															<TableCell align="center">Availability</TableCell>
															<TableCell>Amount</TableCell>
														</TableRow>
													</TableHead>
													<ReactSortable
														tag="tbody"
														list={item.items}
														handle=".sortHandle"
														group={{
															name: "nested",
															pull: true,
															put: ["nested"],
														}}
														onEnd={(event: SortableEvent) => {
															handleItemDragDrop(event);
														}}
														disabled={isViewMode}
														setList={() => {}}
														id={`test-${index}`} // required for drag and drop - Do Not Remove
													>
														{item.items.map(
															(
																subItem: ProcurementMethodSelectionItem,
																subItemIndex: number
															) => (
																<TableRow
																	key={subItem.id}
																	id={`subItem-${subItemIndex}`} // required for drag and drop - Do Not Remove
																	style={{
																		height: "35px",
																	}}
																>
																	<TableCell>
																		{!isItemSourcingTypeCombined && isEditMode && (
																			<span className="sortHandle">
																				<Avatar
																					variant="square"
																					src={dragIcon}
																					alt="Change"
																					sx={{
																						width: 20,
																						height: 20,
																					}}
																					title="Change"
																				/>
																			</span>
																		)}
																	</TableCell>
																	<TableCell
																		align="center"
																		className="col-white-space-normal"
																	>
																		{subItem.item}
																	</TableCell>
																	<TableCell align="center">
																		{subItem.itemAvailability}
																	</TableCell>
																	<TableCell>
																		{Shared.getFormattedCurrency(
																			subItem.totalEstCost
																		)}
																	</TableCell>
																</TableRow>
															)
														)}
													</ReactSortable>
												</Table>
											</Collapse>
										</TableCell>
									</TableRow>
								</React.Fragment>
							</TableBody>
						</Table>
					</div>
				</TableContainer>
			))}
			{openProcurementMethodSelectionDialog && (
				<ProcurementMethodSelectionDialog
					open={openProcurementMethodSelectionDialog}
					selectedProcurementMethodId={
						(isItemSourcingTypeCombined
							? itemListGroupByCombined
							: itemListGroupBySplit)[selectedIndex].procurementMethodId || 0
					}
					totalAmount={ProcurementMethodUtil.getTotalByItemList(
						(isItemSourcingTypeCombined
							? itemListGroupByCombined
							: itemListGroupBySplit)[selectedIndex].items
					)}
					handleCloseDialog={handleMethodSelectionDialogClose}
				/>
			)}
			{openTransversalProcConfirmationDialog && (
				<ConfirmationDialog
					open={openTransversalProcConfirmationDialog}
					positiveActionText="Transversal"
					negativeActionText="Assign"
					title="Transversal Procurement Confirmation"
					message={`There is an existing transversal contract for the selected item ${itemWithTransversalContract}. Click the Transversal button to use the transversal contract. Or, click the Assign button to ignore the transversal contract and use the system-assigned method.`}
					handleDialogClose={handleTransversalProcConfirmationDialogClose}
				/>
			)}
		</div>
	);
};

export default ProcurementMethodSelection;
